import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import holidays from '@date/holidays-us';
import { DateTime } from 'luxon';
import { dayKeysBetween } from '../../../helpers/bookedSlots';

interface PriceDate {
  date: string;
  price: number;
  hourPrice?: number;
  isAvailable?: boolean;
}

interface SelectedDate {
  date: string;
  price: string;
  isAvailable: boolean;
}

interface Booking {
  id: string;
  startAt: number; // UTC milliseconds
  endAt: number; // UTC milliseconds
  status: string;
}

interface Unavailability {
  id: string;
  startAt: number; // UTC milliseconds
  endAt: number; // UTC milliseconds
  reason?: string | null;
  note?: string | null;
}

/** Rates used for days the price calendar has no entry for (Flutter `defaultPrice`). */
export interface FallbackPrices {
  weekday?: number | null;
  weekend?: number | null;
  holiday?: number | null;
}

interface CalendarComponentProps {
  priceCalendar: PriceDate[];
  priceKey?: 'price' | 'hourPrice';
  selectedDates: SelectedDate[];
  onSelectedDatesChange: (dates: SelectedDate[]) => void;
  bookings?: Booking[];
  unavailabilities?: Unavailability[];
  timezone?: string;
  updatingDates?: Set<string>;
  /** Days that just saved: show a success check for a moment. */
  flashDates?: Set<string>;
  fallbackPrices?: FallbackPrices;
}

/** How a date participates in one or more spans (trips or blocks). */
export interface DaySpan {
  /** A span starts on this day and continues to a later day. */
  start: boolean;
  /** A span that started on an earlier day ends on this day. */
  end: boolean;
  /** This day sits strictly inside a span. */
  mid: boolean;
  /** A span that starts and ends on this same day. */
  single: boolean;
}

const emptySpan = (): DaySpan => ({ start: false, end: false, mid: false, single: false });

/** The API sends UTC milliseconds as numbers; tolerate numeric strings and ISO dates. */
export function toMillis(value: number | string | null | undefined): number {
  if (typeof value === 'number') return value;
  if (value == null || value === '') return NaN;
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? Date.parse(value) : asNumber;
}

/**
 * Marks every day between startAt and endAt (inclusive, in the car's timezone)
 * on the map, merging with spans already recorded for the same day so that a
 * trip ending on the day another begins keeps both dots.
 */
export function addSpanToMap(
  map: Map<string, DaySpan>,
  startAt: number | string,
  endAt: number | string,
  timezone: string,
): void {
  const startMs = toMillis(startAt);
  const endMs = toMillis(endAt);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return;
  let current = DateTime.fromMillis(startMs).setZone(timezone).startOf('day');
  const end = DateTime.fromMillis(endMs).setZone(timezone).startOf('day');
  if (!current.isValid || !end.isValid || end < current) return;

  const startKey = current.toISODate();
  const endKey = end.toISODate();
  const isSingleDay = startKey === endKey;

  while (current <= end) {
    const key = current.toISODate();
    if (key) {
      const span = map.get(key) ?? emptySpan();
      if (isSingleDay) span.single = true;
      else if (key === startKey) span.start = true;
      else if (key === endKey) span.end = true;
      else span.mid = true;
      map.set(key, span);
    }
    current = current.plus({ days: 1 });
  }
}

/**
 * Blocked days: host unavailability ranges plus single days switched off in the
 * price calendar, keyed by calendar day in the car's timezone.
 */
export function buildBlockedMap(
  unavailabilities: Array<Pick<Unavailability, 'startAt' | 'endAt'>>,
  priceCalendar: Array<Pick<PriceDate, 'date' | 'isAvailable'>>,
  timezone: string,
): Map<string, DaySpan> {
  const blockedMap = new Map<string, DaySpan>();
  unavailabilities.forEach(unavailability => {
    addSpanToMap(blockedMap, unavailability.startAt, unavailability.endAt, timezone);
  });
  priceCalendar.forEach(item => {
    if (item.isAvailable === false && !blockedMap.has(item.date)) {
      blockedMap.set(item.date, { ...emptySpan(), single: true });
    }
  });
  return blockedMap;
}

/** Bookings that no longer hold the car are not drawn as trips. */
const NON_TRIP_STATUSES = new Set([
  'CANCELLED_BY_ADMIN',
  'CANCELLED_BY_GUEST_AFTER_TRIP_START',
  'CANCELLED_BY_GUEST_LESS_THAN_24_HOURS_BEFORE_TRIP_START',
  'CANCELLED_BY_GUEST_MORE_THAN_24_HOURS',
  'CANCELLED_BY_HOST',
  'CARD_DECLINED',
]);

export function isTripBooking(booking: Pick<Booking, 'status'>): boolean {
  return !NON_TRIP_STATUSES.has(booking.status);
}

const TILE_HEIGHT = 88;
const LINE_TOP = 40;
const LINE_GAP = 9;

const Wrapper = styled.div`
  width: 100%;
`;

const Nav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const MonthTitle = styled.h3`
  flex: 1 1 auto;
  margin: 0;
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);

  @media (max-width: 640px) {
    font-size: 18px;
  }
`;

const NavButton = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg);
  color: var(--color-text-primary);
  transition:
    background-color var(--transition-normal),
    border-color var(--transition-normal);

  &:hover:not(:disabled) {
    background: var(--color-bg-secondary);
    border-color: var(--color-border-strong);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary);

  > span {
    padding: 0 10px 8px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
`;

const Blank = styled.div`
  height: ${TILE_HEIGHT}px;

  @media (max-width: 640px) {
    height: 76px;
  }
`;

const DayCell = styled.button<{
  $selected?: boolean;
  $today?: boolean;
  $holiday?: boolean;
  $edgeTop?: boolean;
  $edgeLeft?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  height: ${TILE_HEIGHT}px;
  padding: 10px;
  text-align: left;
  background: var(--color-bg);
  border: 0 solid var(--color-border);
  border-right-width: 1px;
  border-bottom-width: 1px;
  ${({ $edgeTop }) => $edgeTop && 'border-top-width: 1px;'}
  ${({ $edgeLeft }) => $edgeLeft && 'border-left-width: 1px;'}
  color: var(--color-text-primary);
  transition: background-color var(--transition-fast);
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;

  &:hover {
    background: var(--color-bg-secondary);
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--color-black);
  }

  ${({ $selected }) =>
    $selected &&
    css`
      background: rgb(var(--color-black-rgb) / 0.04);
      &:hover {
        background: rgb(var(--color-black-rgb) / 0.06);
      }
    `}

  @media (max-width: 640px) {
    height: 76px;
    padding: 8px;
  }
`;

const DayNumber = styled.span<{ $today?: boolean; $holiday?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 4px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $holiday }) => ($holiday ? 'var(--color-warning)' : 'inherit')};

  ${({ $today }) =>
    $today &&
    css`
      background: var(--color-black);
      color: var(--color-on-primary);
      font-weight: 600;
    `}
`;

const TileBody = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const PriceTag = styled.div<{ $muted?: boolean }>`
  position: absolute;
  left: 10px;
  bottom: 9px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $muted }) => ($muted ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)')};
  white-space: nowrap;

  @media (max-width: 640px) {
    left: 8px;
    bottom: 7px;
    font-size: 12px;
  }
`;

const PriceSuffix = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: var(--color-text-secondary);
`;

type SpanTone = 'trip' | 'blocked';

const toneColor = (tone: SpanTone) =>
  tone === 'trip' ? 'var(--color-black)' : 'var(--color-text-tertiary)';

const SpanRow = styled.div<{ $tone: SpanTone; $top: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({ $top }) => $top}px;
  height: 0;
  color: ${({ $tone }) => toneColor($tone)};
`;

const Seg = styled.span<{ $from: string; $to: string }>`
  position: absolute;
  top: -1px;
  height: 2px;
  left: ${({ $from }) => $from};
  right: ${({ $to }) => $to};
  background: currentColor;
`;

const Dot = styled.span<{ $at: string }>`
  position: absolute;
  top: -4px;
  left: ${({ $at }) => $at};
  width: 8px;
  height: 8px;
  margin-left: -4px;
  border-radius: 999px;
  background: currentColor;
`;

/**
 * One horizontal span line inside a cell: the segment runs to the cell edges
 * so it joins the neighbouring cells, and a dot marks where a span starts or ends.
 */
const SpanLine = ({ span, tone, top }: { span: DaySpan; tone: SpanTone; top: number }) => (
  <SpanRow $tone={tone} $top={top} data-span={tone}>
    {(span.mid || span.end) && <Seg $from="0" $to="50%" />}
    {(span.mid || span.start) && <Seg $from="50%" $to="0" />}
    {span.single && <Seg $from="28%" $to="28%" />}
    {(span.start || span.end) && <Dot $at="50%" />}
    {span.single && (
      <>
        <Dot $at="28%" />
        <Dot $at="72%" />
      </>
    )}
  </SpanRow>
);

const CheckMark = styled.div<{ $success?: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${({ $success }) => ($success ? 'var(--color-success)' : 'var(--color-black)')};
  animation: ${({ $success }) => ($success ? 'ui-pop-in 150ms ease-out' : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  &::after {
    content: '';
    width: 4px;
    height: 8px;
    border: solid var(--color-on-primary);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    margin-bottom: 2px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgb(255 255 255 / 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid var(--color-border);
  border-top-color: var(--color-black);
  border-radius: 50%;
  animation: price-calendar-spin 0.8s linear infinite;

  @keyframes price-calendar-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 20px;
  margin-top: 12px;
  font-size: 12px;
  color: var(--color-text-secondary);
`;

const LegendItem = styled.span<{ $tone?: SpanTone }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  ${({ $tone }) =>
    $tone &&
    css`
      &::before {
        content: '';
        width: 28px;
        height: 2px;
        background: ${toneColor($tone)};
        box-shadow:
          -4px 0 0 0 ${toneColor($tone)},
          4px 0 0 0 ${toneColor($tone)};
        border-radius: 999px;
      }
    `}
`;

const LegendToday = styled.span`
  display: inline-flex;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: var(--color-black);
`;

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ChevronLeft = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <path d="M10 3 5 8l5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <path d="m6 3 5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type GridCell = { kind: 'blank' } | { kind: 'day'; date: DateTime; key: string };

/**
 * Weeks of a month, Sunday first. Days before `minDay`, after `maxDay` or outside
 * the month are blank; weeks that are entirely blank are dropped (the reference
 * calendar starts at today's week).
 */
export function buildMonthGrid(month: DateTime, minDay: DateTime, maxDay: DateTime): GridCell[][] {
  const first = month.startOf('month');
  const last = month.endOf('month').startOf('day');
  let cursor = first.minus({ days: first.weekday % 7 }); // back to Sunday
  const weeks: GridCell[][] = [];

  while (cursor <= last) {
    const week: GridCell[] = [];
    let hasDay = false;
    for (let i = 0; i < 7; i++) {
      const day = cursor.plus({ days: i });
      const inMonth = day >= first && day <= last;
      const inRange = day >= minDay && day <= maxDay;
      if (inMonth && inRange) {
        week.push({ kind: 'day', date: day, key: day.toISODate() || '' });
        hasDay = true;
      } else {
        week.push({ kind: 'blank' });
      }
    }
    if (hasDay) weeks.push(week);
    cursor = cursor.plus({ days: 7 });
  }
  return weeks;
}

/**
 * Price for a day without a price-calendar row: the holiday, weekend or weekday
 * rate, the same way the server seeds the calendar.
 */
export function fallbackPriceFor(day: DateTime, fallback?: FallbackPrices): number | null {
  if (!fallback) return null;
  const isHoliday = holidays.isHoliday(day.toJSDate());
  const isWeekend = day.weekday === 6 || day.weekday === 7;
  const value = isHoliday
    ? (fallback.holiday ?? fallback.weekday)
    : isWeekend
      ? (fallback.weekend ?? fallback.weekday)
      : fallback.weekday;
  return value == null ? null : value;
}

const EMPTY_SET = new Set<string>();

const CalendarComponent: React.FC<CalendarComponentProps> = ({
  priceCalendar,
  priceKey = 'price',
  selectedDates,
  onSelectedDatesChange,
  bookings = [],
  unavailabilities = [],
  timezone = 'America/Los_Angeles',
  updatingDates = EMPTY_SET,
  flashDates = EMPTY_SET,
  fallbackPrices,
}) => {
  // "Today" is the listing's today, as in the Flutter host calendar.
  const today = DateTime.fromISO(DateTime.now().setZone(timezone).toISODate() || '').startOf('day');
  const maxDay = today.plus({ years: 2 });
  const [viewMonth, setViewMonth] = useState(() => today.startOf('month'));
  const canGoBack = viewMonth > today.startOf('month');
  const canGoForward = viewMonth < maxDay.startOf('month');

  const priceMap = new Map(
    priceCalendar.map(item => [
      item.date,
      priceKey === 'hourPrice' ? (item.hourPrice ?? item.price) : item.price,
    ]),
  );
  const availabilityMap = new Map(priceCalendar.map(item => [item.date, item.isAvailable ?? true]));
  const selectedSet = new Set(selectedDates.map(item => item.date));

  // Trips (black) and blocks (gray), keyed by calendar day in the car's timezone.
  const tripMap = new Map<string, DaySpan>();
  bookings.filter(isTripBooking).forEach(booking => {
    addSpanToMap(tripMap, booking.startAt, booking.endAt, timezone);
  });

  const blockedMap = buildBlockedMap(unavailabilities, priceCalendar, timezone);

  const weeks = buildMonthGrid(viewMonth, today, maxDay);

  const effectivePrice = (dateStr: string): number | null => {
    const stored = priceMap.get(dateStr);
    if (stored !== undefined && stored !== null) return stored;
    return fallbackPriceFor(DateTime.fromISO(dateStr), fallbackPrices);
  };

  const entryFor = (dateStr: string): SelectedDate => ({
    date: dateStr,
    price: effectivePrice(dateStr)?.toString() ?? '',
    isAvailable: availabilityMap.get(dateStr) ?? true,
  });

  const toggleDate = (dateStr: string) => {
    if (selectedSet.has(dateStr)) {
      onSelectedDatesChange(selectedDates.filter(item => item.date !== dateStr));
    } else {
      onSelectedDatesChange([...selectedDates, entryFor(dateStr)]);
    }
  };

  /** Shift-click (Flutter long-press): select every day from the latest selected day on or before. */
  const extendSelectionTo = (dateStr: string) => {
    const anchor = selectedDates
      .map(item => item.date)
      .filter(date => date <= dateStr)
      .sort()
      .pop();
    if (!anchor) {
      toggleDate(dateStr);
      return;
    }
    const additions = dayKeysBetween(anchor, dateStr).filter(key => !selectedSet.has(key));
    onSelectedDatesChange([...selectedDates, ...additions.map(entryFor)]);
  };

  // Long-press (Flutter `_onDayLongPressed`): fill the range from the latest selected
  // day on or before the pressed day. Works with touch and mouse; shift-click does the same.
  const LONG_PRESS_MS = 450;
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);

  const cancelLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const startLongPress = (dateStr: string) => {
    cancelLongPress();
    longPressFired.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      longPressFired.current = true;
      extendSelectionTo(dateStr);
    }, LONG_PRESS_MS);
  };

  const handleDayClick = (event: React.MouseEvent, dateStr: string) => {
    if (longPressFired.current) {
      // The press already selected the range; swallow the click that follows it.
      longPressFired.current = false;
      return;
    }
    if (event.shiftKey) extendSelectionTo(dateStr);
    else toggleDate(dateStr);
  };

  const hasBlocks = blockedMap.size > 0;

  return (
    <Wrapper>
      <Nav>
        <MonthTitle>{viewMonth.toFormat('LLLL yyyy')}</MonthTitle>
        <NavButton
          type="button"
          aria-label="Previous month"
          disabled={!canGoBack}
          onClick={() => setViewMonth(m => m.minus({ months: 1 }))}
        >
          <ChevronLeft />
        </NavButton>
        <NavButton
          type="button"
          aria-label="Next month"
          disabled={!canGoForward}
          onClick={() => setViewMonth(m => m.plus({ months: 1 }))}
        >
          <ChevronRight />
        </NavButton>
      </Nav>

      <Weekdays aria-hidden="true">
        {WEEKDAY_LABELS.map(label => (
          <span key={label}>{label}</span>
        ))}
      </Weekdays>

      <Grid role="grid" aria-label={viewMonth.toFormat('LLLL yyyy')}>
        {weeks.map((week, rowIndex) =>
          week.map((cell, colIndex) => {
            if (cell.kind === 'blank') {
              return <Blank key={`${rowIndex}-${colIndex}`} aria-hidden="true" />;
            }
            const dateStr = cell.key;
            const price = effectivePrice(dateStr);
            const isSelected = selectedSet.has(dateStr);
            const isUpdating = updatingDates.has(dateStr);
            const isFlashing = flashDates.has(dateStr);
            const trip = tripMap.get(dateStr);
            const blocked = blockedMap.get(dateStr);
            const isBlockedDay = Boolean(blocked) || availabilityMap.get(dateStr) === false;
            const above = rowIndex > 0 ? weeks[rowIndex - 1][colIndex] : undefined;
            const left = colIndex > 0 ? week[colIndex - 1] : undefined;
            const classes = ['calendar-day'];
            if (isSelected) classes.push('selected-date');
            if (trip) classes.push('booking-date');
            if (blocked) classes.push('blocked-date');

            return (
              <DayCell
                key={dateStr}
                type="button"
                role="gridcell"
                className={classes.join(' ')}
                data-date={dateStr}
                aria-pressed={isSelected}
                aria-label={cell.date.toFormat('cccc, LLLL d')}
                $selected={isSelected}
                $edgeTop={!above || above.kind === 'blank'}
                $edgeLeft={!left || left.kind === 'blank'}
                onClick={event => handleDayClick(event, dateStr)}
                onPointerDown={event => {
                  if (event.button === 0) startLongPress(dateStr);
                }}
                onPointerUp={cancelLongPress}
                onPointerLeave={cancelLongPress}
                onPointerCancel={cancelLongPress}
                onContextMenu={event => event.preventDefault()}
              >
                <DayNumber
                  $today={cell.date.hasSame(today, 'day')}
                  $holiday={holidays.isHoliday(cell.date.toJSDate())}
                >
                  {cell.date.day}
                </DayNumber>
                <TileBody>
                  {trip && <SpanLine span={trip} tone="trip" top={LINE_TOP} />}
                  {blocked && (
                    <SpanLine
                      span={blocked}
                      tone="blocked"
                      top={trip ? LINE_TOP + LINE_GAP : LINE_TOP}
                    />
                  )}

                  {price !== undefined && price !== null && (
                    <PriceTag $muted={isBlockedDay}>
                      ${price}
                      {priceKey === 'hourPrice' && <PriceSuffix>/hr</PriceSuffix>}
                    </PriceTag>
                  )}

                  {isFlashing && !isUpdating && <CheckMark $success aria-hidden="true" />}
                  {isSelected && !isUpdating && !isFlashing && <CheckMark aria-hidden="true" />}

                  {isUpdating && (
                    <LoadingOverlay>
                      <LoadingSpinner />
                    </LoadingOverlay>
                  )}
                </TileBody>
              </DayCell>
            );
          }),
        )}
      </Grid>

      <Legend aria-hidden="true">
        <LegendItem>
          <LegendToday /> Today
        </LegendItem>
        <LegendItem $tone="trip">Trip</LegendItem>
        {hasBlocks && <LegendItem $tone="blocked">Unavailable</LegendItem>}
      </Legend>
    </Wrapper>
  );
};

export type { SelectedDate };
export default CalendarComponent;
